var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import ToastUtils from '@/utils/ToastUtils';
import useAppComponents from './useAppComponents';
import { USER_TUTORIAL_PROGRESS_QUERY_KEY } from './useUserTutorialProgress';
export function useUpvoteToggle({ initialIsUpvoted, initialTotalCount, targetId, targetType, placeId, onSuccess, }) {
    const { api } = useAppComponents();
    const queryClient = useQueryClient();
    const [isUpvoted, setIsUpvoted] = useState(initialIsUpvoted);
    const [totalUpvoteCount, setTotalUpvoteCount] = useState(initialTotalCount !== null && initialTotalCount !== void 0 ? initialTotalCount : 0);
    useEffect(() => {
        setIsUpvoted(initialIsUpvoted);
    }, [initialIsUpvoted]);
    useEffect(() => {
        setTotalUpvoteCount(initialTotalCount !== null && initialTotalCount !== void 0 ? initialTotalCount : 0);
    }, [initialTotalCount]);
    useEffect(() => {
        setIsUpvoted(initialIsUpvoted);
        setTotalUpvoteCount(initialTotalCount !== null && initialTotalCount !== void 0 ? initialTotalCount : 0);
    }, [targetId, initialIsUpvoted, initialTotalCount]);
    const { mutate, isPending } = useMutation({
        mutationFn: (currentIsUpvoted) => __awaiter(this, void 0, void 0, function* () {
            if (!targetId)
                throw new Error('targetId is required');
            if (currentIsUpvoted) {
                return yield api.cancelUpvotePost({
                    id: targetId,
                    targetType,
                });
            }
            else {
                return yield api.giveUpvotePost({
                    id: targetId,
                    targetType,
                });
            }
        }),
        onMutate: (wasUpvoted) => __awaiter(this, void 0, void 0, function* () {
            setIsUpvoted(!wasUpvoted);
            setTotalUpvoteCount(prev => Math.max(0, prev + (wasUpvoted ? -1 : 1)));
        }),
        onSuccess: (_data, wasUpvoted) => {
            if (isUpvoted) {
                ToastUtils.show('소중한 의견 감사해요 👍');
            }
            if (!placeId) {
                return;
            }
            if (targetType === 'PLACE' ||
                targetType === 'PLACE_ACCESSIBILITY' ||
                targetType === 'BUILDING_ACCESSIBILITY') {
                queryClient.invalidateQueries({
                    queryKey: ['PlaceDetailV2', placeId, 'Accessibility'],
                });
                // 정복한 장소 > 도움이 돼요 리스트
                queryClient.invalidateQueries({
                    queryKey: ['PlacesUpvoted'],
                });
                // 정복한 장소 > 도움이 돼요 통계
                queryClient.invalidateQueries({
                    queryKey: ['UpvotedForNumberOfItems'],
                });
            }
            // 윌리의 외출 NUX 튜토리얼: 도움이 돼요 미션 진행 상태 무효화
            queryClient.invalidateQueries({
                queryKey: USER_TUTORIAL_PROGRESS_QUERY_KEY,
            });
            if (targetType === 'PLACE_REVIEW' || targetType === 'TOILET_REVIEW') {
                queryClient.invalidateQueries({
                    queryKey: ['PlaceDetailV2', placeId, targetType],
                });
                // 내 리뷰 > 내가 작성한 리뷰 리스트
                queryClient.invalidateQueries({
                    queryKey: ['MyReviews', targetType],
                });
                // 내 리뷰 > 도움이 돼요 리스트
                queryClient.invalidateQueries({
                    queryKey: ['ReviewsUpvoted', targetType],
                });
                // 내 리뷰 > 내가 작성한 리뷰, 도움이 돼요 통계
                queryClient.invalidateQueries({
                    queryKey: ['ReviewHistory', 'Upvote', targetType],
                });
            }
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess({ wasUpvoted });
        },
        onError: (error, wasUpvoted) => {
            setIsUpvoted(wasUpvoted);
            setTotalUpvoteCount(prev => Math.max(0, prev + (wasUpvoted ? 1 : -1)));
            ToastUtils.showOnApiError(error);
        },
    });
    const toggleUpvote = () => {
        if (isPending || !targetId) {
            return;
        }
        mutate(isUpvoted);
    };
    return {
        isUpvoted,
        totalUpvoteCount,
        toggleUpvote,
    };
}
